import { useClientStorageFlag } from '@/entities/clientStorage';
import { useCurrentSpaceId, useGetSpaceCredits } from '@/entities/teamspace';

import { RECORDING_ANNOUNCEMENT_STORAGE_KEY } from '../lib/constants';
import { isPaidBeforeRecordingRelease } from '../lib/eligibility';
import { useIsRecordingAnnouncementViewport } from './useIsRecordingAnnouncementViewport';

type RecordingAnnouncementRedirect = {
  markRedirected: () => void;
  shouldRedirect: boolean;
  status: 'loading' | 'resolved';
};

export const useRecordingAnnouncementRedirect = (): RecordingAnnouncementRedirect => {
  const isWideScreen = useIsRecordingAnnouncementViewport();
  const spaceId = useCurrentSpaceId();
  const credits = useGetSpaceCredits(isWideScreen ? spaceId : '');

  const isBalancePending = Boolean(spaceId) && credits.isPending;
  const isExistingPaidSpace = !isBalancePending && isPaidBeforeRecordingRelease(credits.data?.subscription);

  const flag = useClientStorageFlag(RECORDING_ANNOUNCEMENT_STORAGE_KEY, {
    enabled: isWideScreen && isExistingPaidSpace,
  });

  const resolved = (shouldRedirect: boolean): RecordingAnnouncementRedirect => ({
    markRedirected: flag.markAsShown,
    shouldRedirect,
    status: 'resolved',
  });

  if (!isWideScreen) return resolved(false);

  if (isBalancePending) return { markRedirected: flag.markAsShown, shouldRedirect: false, status: 'loading' };

  if (!isExistingPaidSpace) return resolved(false);

  if (flag.isLoading) return { markRedirected: flag.markAsShown, shouldRedirect: false, status: 'loading' };

  if (flag.isError) return resolved(false);

  return resolved(!flag.hasShown);
};
